/**
 * Mobile Option Sheets
 *
 * Replaces native <select> pickers on mobile with bottom-sheet modals
 * containing a grid of tappable option buttons (sizes, colors, etc.).
 *
 * On the PDP the sheets are moved into the fixed actions row on init, so
 * `position: absolute; bottom: 100%` floats them above the bar without being
 * trapped by the containing block that backdrop-filter creates on ancestors.
 *
 * `init()` and `initAtcPlusFit()` are re-runnable and accept a root element, so
 * markup that arrives after load can be wired up too — the compare page fetches
 * its variant picker and buy buttons per slot (assets/section-pdp-compare.js)
 * and calls both through `window.MobileOptionSheets`.
 */

(function () {
  const IS_MOBILE_MQ = window.matchMedia('(max-width: 1024px)');

  let suppressScrollClose = false;
  let lastScrollY = window.scrollY;

  /* Every trigger–sheet pair wired so far, across all init() calls. The
     backdrop hit-test below searches this rather than the current call's pairs,
     so on the compare page — where the pairs arrive in two separate injections
     — a tap over the other product's trigger still switches sheets. */
  const allPairs = [];

  /* ------------------------------------------------------------------
     Open / Close helpers
     ------------------------------------------------------------------ */
  /* Publish the open panel's height so elements OUTSIDE the sheet's subtree can
     clear it. The consumer is the Salesforce chat launcher, a fixed sibling
     elsewhere in the DOM that cannot inherit from the sheet — see the clearance
     block in snippets/salesforce-chat.liquid. Same contract as
     --compare-bar-height in assets/facets-inline.css. Measured rather than
     hard-coded because the panel grows with the number of options. */
  function publishSheetHeight(sheet) {
    const panel = sheet && sheet.querySelector('.mobile-option-sheet__panel');
    const height = panel ? Math.round(panel.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--option-sheet-height', `${height}px`);
  }

  function openSheet(sheet) {
    if (!sheet) return;
    sheet.classList.add('is-open');
    sheet.setAttribute('aria-hidden', 'false');
    publishSheetHeight(sheet);
  }

  function closeSheet(sheet) {
    if (!sheet) return;
    sheet.classList.remove('is-open');
    sheet.setAttribute('aria-hidden', 'true');
    publishSheetHeight(null);
  }

  function closeAllSheets() {
    document.querySelectorAll('.mobile-option-sheet.is-open').forEach((s) => {
      closeSheet(s);
    });
  }

  function scrollSheetIntoView(sheet) {
    const bar = document.querySelector('.product__actions-row');
    const panel = sheet.querySelector('.mobile-option-sheet__panel');
    if (!bar || !panel || bar.classList.contains('is-bar-fixed')) return;

    const panelRect = panel.getBoundingClientRect();
    const vh = window.innerHeight;
    const fullyVisible = panelRect.top >= 0 && panelRect.bottom <= vh;
    if (fullyVisible) return;

    const targetTop = panelRect.top + window.scrollY;
    const scrollTo = targetTop - (vh - panelRect.height) / 2;

    suppressScrollClose = true;
    window.scrollTo({ top: Math.max(0, scrollTo), behavior: 'smooth' });
    window.setTimeout(() => {
      suppressScrollClose = false;
    }, 600);
  }

  function openSheetAndScroll(sheet) {
    openSheet(sheet);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollSheetIntoView(sheet);
      });
    });
  }

  /* ------------------------------------------------------------------
     Update the main product form when an option is selected
     ------------------------------------------------------------------ */
  function selectMainOption(trigger, value) {
    const sel = trigger.querySelector('.select__select');
    if (sel) {
      for (let i = 0; i < sel.options.length; i++) {
        if (sel.options[i].value === value || sel.options[i].textContent.trim() === value) {
          sel.value = sel.options[i].value;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
    }
  }

  /* ------------------------------------------------------------------
     Update the visible label in the compact bar
     ------------------------------------------------------------------ */
  function updateTriggerLabel(trigger, value, swatchCss) {
    const labelValue = trigger.querySelector('.form__label-value');
    if (labelValue) {
      const swatchEl = labelValue.querySelector('.form__label-swatch');
      if (swatchEl && swatchCss) {
        swatchEl.setAttribute('style', swatchCss);
      }
      // Update the text – replace only the text node (preserve the swatch span)
      const textNodes = [];
      labelValue.childNodes.forEach((n) => {
        if (n.nodeType === Node.TEXT_NODE) textNodes.push(n);
      });
      if (textNodes.length > 0) {
        textNodes[textNodes.length - 1].textContent = ' ' + value + ' ';
      } else if (!swatchEl) {
        labelValue.textContent = value;
      }
    }
  }

  /* ------------------------------------------------------------------
     Close open sheets when the user scrolls the page
     ------------------------------------------------------------------ */
  function initScrollToClose() {
    window.addEventListener(
      'scroll',
      () => {
        if (!document.querySelector('.mobile-option-sheet.is-open')) return;
        if (suppressScrollClose) {
          lastScrollY = window.scrollY;
          return;
        }

        if (Math.abs(window.scrollY - lastScrollY) > 8) closeAllSheets();
        lastScrollY = window.scrollY;
      },
      { passive: true }
    );
  }

  /* ------------------------------------------------------------------
     Initialise triggers & sheet interactions
     ------------------------------------------------------------------ */
  function init(root) {
    const scope = root || document;

    // Build a map of trigger → sheet before moving any sheet
    const triggerSheetPairs = [];

    scope.querySelectorAll('[data-mobile-sheet-trigger]').forEach((trigger) => {
      // Re-entrant: a trigger wired by an earlier call must not collect a
      // second set of listeners.
      if (trigger.dataset.sheetBound === 'true') return;
      const sheetName = trigger.dataset.mobileSheetTrigger;
      const sheet = trigger.querySelector('[data-mobile-sheet="' + sheetName + '"]');
      if (!sheet) return;
      trigger.dataset.sheetBound = 'true';
      // Which picker owns this sheet, for the change-sync below. Needed because
      // the compare page puts two products on one page and their sheets carry
      // identical data-option-name values.
      const owner = trigger.closest('[data-section]');
      if (owner) sheet.dataset.sheetOwner = owner.dataset.section;
      triggerSheetPairs.push({ trigger, sheet });
    });

    allPairs.push(...triggerSheetPairs);

    // Move the sheets into .product__actions-row so they can use
    // position: absolute; bottom: 100% to float above the fixed bar.
    // This avoids the containing-block created by backdrop-filter on variant-selects.
    //
    // Only when that row exists. Without it (compare page) the sheet stays put
    // and resolves against its own positioned ancestor — .pdp-compare__actions.
    // Moving it to <body> instead, as this used to do unconditionally, would
    // anchor `bottom: 100%` to the document and throw the panel far above the
    // top of the page.
    const actionsRow = document.querySelector('.product__actions-row');
    if (actionsRow) {
      triggerSheetPairs.forEach((pair) => {
        actionsRow.appendChild(pair.sheet);
      });
    }

    // Wire up each trigger–sheet pair
    triggerSheetPairs.forEach((pair) => {
      const trigger = pair.trigger;
      const sheet = pair.sheet;

      // Tap on the cell opens the sheet (mobile only)
      // Close any other open sheet first so only one is visible at a time
      trigger.addEventListener('click', (e) => {
        if (!IS_MOBILE_MQ.matches) return;
        e.preventDefault();
        e.stopPropagation();
        closeAllSheets();
        openSheetAndScroll(sheet);
      });

      // Close buttons + the full-viewport backdrop (interactive while open
      // since AAS-4277 — tap anywhere outside the panel dismisses, alongside
      // scroll-to-close)
      sheet.querySelectorAll('[data-close-sheet]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          // A closer that ALSO opens an info-drawer (the sheet's "Size
          // guide" action) must let the click bubble — the drawer's
          // [data-drawer-open] wiring lives on document, and stopping
          // propagation here left the button dead (AAS-5265). Everything
          // else keeps the stop so a dismissing tap can't trigger anything
          // underneath.
          if (!btn.hasAttribute('data-drawer-open')) e.stopPropagation();

          // The backdrop covers the other size/color triggers (both the
          // in-form selects and the bar cells), so tapping the second
          // trigger used to only dismiss — and needed a second tap to open.
          // Hit-test through the backdrop: a tap landing on another trigger
          // switches to its sheet in the same tap (AAS-5265).
          if (btn.classList.contains('mobile-option-sheet__backdrop')) {
            const under = document
              .elementsFromPoint(e.clientX, e.clientY)
              .find((node) => node !== btn && node.closest && node.closest('[data-mobile-sheet-trigger]'));
            const targetTrigger = under && under.closest('[data-mobile-sheet-trigger]');
            if (targetTrigger) {
              const targetPair = allPairs.find((p) => p.trigger === targetTrigger);
              if (targetPair && targetPair.sheet !== sheet) {
                closeAllSheets();
                openSheetAndScroll(targetPair.sheet);
                return;
              }
            }
          }

          closeSheet(sheet);
        });
      });

      // Option buttons (size / generic)
      sheet.querySelectorAll('.mobile-option-sheet__btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          // AAS-5053 follow-up: unavailable ("is-unavailable") is a visual,
          // struck-through state only — same as the desktop swatch picker
          // (product-variant-options.liquid), which never blocks the click.
          // Selecting a sold-out combination here switches to that variant
          // and the CTA correctly reports "Sold out" / offers "Notify me",
          // matching desktop instead of silently doing nothing.
          const val = btn.dataset.sheetOptionValue;
          sheet.querySelectorAll('.mobile-option-sheet__btn').forEach((b) => {
            b.classList.remove('is-selected');
          });
          btn.classList.add('is-selected');
          selectMainOption(trigger, val);
          updateTriggerLabel(trigger, val);
          setTimeout(() => {
            closeSheet(sheet);
          }, 150);
        });
      });

      // Color buttons
      sheet.querySelectorAll('.mobile-option-sheet__color-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          // AAS-5053 follow-up: same non-blocking "is-unavailable" treatment
          // as the size buttons above.
          const val = btn.dataset.sheetOptionValue;
          // Display label: the native Shopify option value (`val`), so casing matches the
          // Variants editor. The PIM name (data-pim-color) is no longer used — it came
          // through ALL CAPS from Salsify. Selection also uses `val`, unchanged.
          const label = val;
          const swatchCss = btn.dataset.sheetSwatchCss || '';
          sheet.querySelectorAll('.mobile-option-sheet__color-btn').forEach((b) => {
            b.classList.remove('is-selected');
          });
          btn.classList.add('is-selected');
          selectMainOption(trigger, val);
          updateTriggerLabel(trigger, label, swatchCss);
          setTimeout(() => {
            closeSheet(sheet);
          }, 150);
        });
      });
    });

    bindGlobals();
  }

  /* ------------------------------------------------------------------
     Document-level listeners — bound on the first init() only, since init()
     is re-entrant for injected markup and these would otherwise stack up.
     ------------------------------------------------------------------ */
  let globalsBound = false;

  function bindGlobals() {
    if (globalsBound) return;
    globalsBound = true;

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAllSheets();
    });

    initScrollToClose();

    // Sync: listen for variant changes from other sources (desktop picker,
    // sticky bar, etc.) and update bottom sheet selection state
    document.body.addEventListener('change', (e) => {
      const target = e.target;
      if (target.tagName !== 'INPUT' && target.tagName !== 'SELECT') return;

      const optionName = target.name;
      const matches = optionName.match(/options\[(.+)\]/);
      const displayName = matches ? matches[1] : optionName;
      const value =
        target.tagName === 'INPUT'
          ? target.value
          : target.options[target.selectedIndex]
            ? target.options[target.selectedIndex].value
            : '';

      if (!value) return;

      const ownerEl = target.closest('[data-section]');
      const ownerSection = ownerEl ? ownerEl.dataset.section : null;

      document.querySelectorAll('.mobile-option-sheet').forEach((sheet) => {
        if (sheet.dataset.optionName !== displayName) return;
        /* Also match the owning picker. On the compare page both products'
           sheets answer to data-option-name "Size", so the page-wide sweep
           marked the other product's buttons as selected too. Either side
           missing falls back to the old behaviour, which is what the PDP —
           single picker per page — has always done. */
        if (ownerSection && sheet.dataset.sheetOwner && sheet.dataset.sheetOwner !== ownerSection) return;
        sheet.querySelectorAll('[data-sheet-option-value]').forEach((btn) => {
          btn.classList.toggle('is-selected', btn.dataset.sheetOptionValue === value);
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     Scroll-triggered sticky bar
     The bar sits in normal document flow. Once it has completely scrolled
     off the top of the viewport, a JS class makes it position: fixed at
     the bottom. A spacer div holds its place to prevent layout shift.
     ------------------------------------------------------------------ */
  function initStickyBar() {
    const bar = document.querySelector('.product__actions-row');
    if (!bar) return;

    const spacer = document.createElement('div');
    spacer.className = 'product__actions-row__spacer';
    bar.parentNode.insertBefore(spacer, bar.nextSibling);

    /* AAS-5522 — the ski template pins its info panel over the packshot with
       `position: relative; z-index: 2` (AAS-5075, section-main-product.css).
       That is a STACKING CONTEXT, and the bar lives inside it: once fixed, its
       own z-index 50 is resolved against the panel's siblings, not the page,
       so it competes as 2. Every section below main-product is
       `.shopify-section { position: relative; z-index: 3 }` — 3 beats 2, and
       the datasheet paints straight over the bar. Boot/protection do not pin
       their panel, so it is `position: static` there, no stacking context is
       created, and 50 wins as intended.
       The panel is far off-screen whenever the bar is fixed, so lifting it for
       exactly that period changes nothing visually and cannot be done in CSS
       alone — a fixed child cannot escape its ancestor's stacking context.
       Toggled with the fix state below; see .product__info-wrapper.has-fixed-bar. */
    const panel = bar.closest('.product__info-wrapper');
    const setPanelLift = (on) => panel && panel.classList.toggle('has-fixed-bar', on);

    // Same element the desktop island watches (pdp-sidebar-ctas.js:235).
    const footer = document.querySelector('footer');

    let isFixed = false;

    /* Exit animation bookkeeping. Removing .is-bar-fixed drops the bar back
       into static flow immediately, so there is nothing left to animate — the
       bar has to STAY fixed while it falls, and only then be unfixed. The
       spacer stays visible for that whole time too, or the page below would
       jump up mid-animation. See pdp-actions-bar-fall in
       section-main-product.css. AAS-4640 */
    let leaveTimer = null;
    let onLeaveEnd = null;

    function cancelLeave() {
      if (!onLeaveEnd) return;
      bar.removeEventListener('animationend', onLeaveEnd);
      clearTimeout(leaveTimer);
      onLeaveEnd = null;
      leaveTimer = null;
      bar.classList.remove('is-bar-leaving');
    }

    function unfix() {
      bar.classList.remove('is-bar-fixed', 'is-bar-leaving');
      spacer.classList.remove('is-visible');
      setPanelLift(false);
    }

    function startLeave() {
      // Reduced motion, or a re-entrant call: unfix without the theatre.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || onLeaveEnd) {
        cancelLeave();
        unfix();
        return;
      }
      bar.classList.add('is-bar-leaving');
      onLeaveEnd = () => {
        cancelLeave();
        unfix();
      };
      bar.addEventListener('animationend', onLeaveEnd);
      // Safety net: animationend can be missed if the tab is backgrounded
      // mid-flight, and the bar must never be left fixed and invisible.
      leaveTimer = setTimeout(onLeaveEnd, 500);
    }

    function update() {
      if (!IS_MOBILE_MQ.matches) {
        if (isFixed) {
          // Leaving the mobile breakpoint entirely — no animation, just drop it.
          cancelLeave();
          unfix();
          isFixed = false;
        }
        return;
      }

      // When fixed the bar is out of flow – check the spacer's position instead
      const rect = isFixed ? spacer.getBoundingClientRect() : bar.getBoundingClientRect();

      /* AAS-5285: the bar also retires once the footer comes into view, the
         same rule the desktop island follows (assets/pdp-sidebar-ctas.js
         _handleScroll: `isTargetOut && !isFooterIn`). Mobile only ever tested
         whether the docked row had scrolled off the top, so the bar rode over
         the footer all the way to the end of the page.
         Read from the live rect rather than an absolute offset: the footer's
         position moves as lazy content below it settles, and a value cached at
         init would go stale. */
      const footerIn = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      const shouldFix = rect.bottom < 0 && !footerIn;

      if (!isFixed && shouldFix) {
        isFixed = true;
        // Scrolled back down while the bar was still falling: abort the exit
        // and let it stay put rather than restarting the rise mid-fall.
        cancelLeave();
        bar.classList.add('is-bar-fixed');
        spacer.classList.add('is-visible');
        setPanelLift(true);
      } else if (isFixed && !shouldFix) {
        isFixed = false;
        startLeave();
      }
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------
     AAS-4277: ATC label fit — when a long label (de/fr) can't share one
     row with the "+" icon, the icon is dropped FIRST (design rule) instead
     of the text wrapping or clipping. Class removal first restores the
     baseline so every re-measure (resize, label swap, font load) starts
     from the same state; toggling the class is an attribute mutation, so
     the childList/characterData observer below cannot loop on it.
     ------------------------------------------------------------------ */
  function initAtcPlusFit(root) {
    /* The PDP measures the button inside its fixed actions row; the compare page
       passes the slot's injected form instead. Same design rule either way. */
    const scope = root || document.querySelector('.product__actions-row');
    if (!scope) return;
    const bar = scope.closest('.product__actions-row');
    const submit = scope.querySelector('.product-form__submit');
    if (!submit) return;
    const plus = submit.querySelector('.product-form__submit-plus');
    const label = submit.querySelector(
      ':scope > span:not(.product-form__submit-plus):not(.buttonPrice):not(.product-form__submit-pricebar)'
    );
    if (!plus || !label) return;

    const measure = () => {
      submit.classList.remove('atc-plus-dropped');
      if (!IS_MOBILE_MQ.matches) return;
      // Either the nowrap row clips (scrollWidth) or the flex-wrap pushed
      // the label onto its own line below the icon (offsetTop).
      let overflows =
        submit.scrollWidth > submit.clientWidth ||
        label.offsetTop > plus.offsetTop;

      /* AAS-5287: docked lets the label wrap, so a long one no longer overflows
         — it just grows taller, and neither test above notices. Count the lines
         instead: the moment the label needs a second one, the "+" gives up its
         space to the text. Measured against the label's own line-height rather
         than a constant, so a type-scale change cannot silently break it. */
      if (!overflows) {
        const lineHeight = parseFloat(getComputedStyle(label).lineHeight);
        if (lineHeight > 0 && label.offsetHeight > lineHeight * 1.5) overflows = true;
      }

      if (overflows) submit.classList.add('atc-plus-dropped');
    };

    window.addEventListener('resize', measure, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    new MutationObserver(measure).observe(submit, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    /* AAS-5287: re-measure when the bar docks or floats. The two states give the
       label different room — docked wraps to a second line, floating stays on
       one — so a fit decided in one is meaningless in the other. Without this
       the class was only ever set at load: docked measured "fits" (it wraps),
       the class came off, and nothing put it back when the bar went floating,
       so a long label clipped there with the "+" still taking space.
       Only where there is a bar to dock — the compare page has none. */
    if (bar) {
      new MutationObserver(measure).observe(bar, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    measure();
  }

  /* Re-init hook for markup that arrives after load. The compare page fetches
     each slot's variant picker and buy buttons separately, long after
     DOMContentLoaded, and calls both of these with the slot container as root
     (assets/section-pdp-compare.js). */
  window.MobileOptionSheets = { init, initAtcPlusFit };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      initStickyBar();
      initAtcPlusFit();
    });
  } else {
    init();
    initStickyBar();
    initAtcPlusFit();
  }
})();
